import { withTranslation } from 'react-i18next';
import React, { Fragment, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import { Container, Label, Row, Col, Card, CardBody } from 'reactstrap';
import MedicalApplicationModal from './components/MedicalApplicationModal';
import {
  medicationApplicationListPendingMedicationsFilterByDate,
  medicationApplicationListFinishedMedicationsFilterByDate,
  saveMutation,
} from '../../shared/queries/MedicationApplication';
import {
  changeLoadingTrue,
  changeLoadingFalse,
} from '../../redux/actions/loadingActions';
import DateSelection from '../../shared/components/form/DateSelection';
import ApplicationColumns from './components/ApplicationColumns';
import FinishedColumns from './components/FinishedColumns';
import MatTable from '../../shared/components/material-table';
import GraphQl from '../../GraphQL';
import { showNotificationAutoDestroy } from '../../shared/components/ShowNotification';
import changePendingMedication from '../../redux/actions/pendingMedicationActions';
import updatePendencySummary from '../../redux/actions/pendencyActions';
import {
  loadPendencyQuantity,
  updatePendency,
} from '../../shared/utils/smartutils';
import {
  ACTION_NAME,
  MEDICATION_APPLICATION_STATUS,
} from '../../shared/utils/constants';

const MedicationApplication = (props) => {
  const [pendingMedications, setPendingMedications] = useState([]);
  const [finishedMedications, setFinishedMedications] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [modal, setModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [applicationType, setApplicationType] = useState();
  const { t, dispatch } = props;

  let pendingApplications;

  function showLoading() {
    dispatch(changeLoadingTrue());
  }

  function hideLoading() {
    dispatch(changeLoadingFalse());
  }

  function listPendingMedications(date) {
    let convertedDate;
    if (date) {
      convertedDate = new Date(date).getTime();
    } else {
      convertedDate = new Date(selectedDate).getTime();
    }
    const query =
      medicationApplicationListPendingMedicationsFilterByDate(convertedDate);
    showLoading();
    GraphQl(query, {})
      .then((res) => {
        res.medicationApplicationList.listOfPendingMedications =
          res.medicationApplicationList.listOfPendingMedications.map(
            (item) => ({
              ...item,
              formattedshouldApplicationDate: format(
                new Date(item.shouldApplicationDate),
                'dd/MM/yyyy'
              ),
            })
          );
        setPendingMedications(
          res.medicationApplicationList.listOfPendingMedications
        );
        updatePendency(dispatch);
        hideLoading();
      })
      .catch((error) => {
        console.error(error);
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  }

  function listFinishedMedications() {
    const query = medicationApplicationListFinishedMedicationsFilterByDate(
      new Date().getTime()
    );
    showLoading();
    GraphQl(query, {})
      .then((res) => {
        res.medicationApplicationList.listOfFinishedMedications =
          res.medicationApplicationList.listOfFinishedMedications.map(
            (item) => ({
              ...item,
              formattedshouldApplicationDate: format(
                new Date(item.shouldApplicationDate),
                'dd/MM/yyyy'
              ),
              formattedApplicationDate: format(
                new Date(item.applicationDate),
                'dd/MM/yyyy'
              ),
              formattedStatus:
                item.applicationStatus === MEDICATION_APPLICATION_STATUS.APPLIED
                  ? t(
                      `medication_application.status.${MEDICATION_APPLICATION_STATUS.APPLIED}`
                    )
                  : t(
                      `medication_application.status.${MEDICATION_APPLICATION_STATUS.CANCELED}`
                    ),
            })
          );
        setFinishedMedications(
          res.medicationApplicationList.listOfFinishedMedications
        );
        hideLoading();
      })
      .catch((error) => {
        console.error(error);
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  }

  function updatePendingMedicationApplications() {
    const query = medicationApplicationListPendingMedicationsFilterByDate(
      new Date().getTime()
    );
    GraphQl(query, {}).then((res) => {
      res.medicationApplicationList.listOfPendingMedications =
        res.medicationApplicationList.listOfPendingMedications;
      pendingApplications =
        res.medicationApplicationList.listOfPendingMedications.length;
      dispatch(changePendingMedication(pendingApplications));
    });

    return pendingApplications;
  }

  const toggleModal = () => {
    setModal(!modal);
  };

  const updateSelectedDate = (date) => {
    setSelectedDate(date);
    listPendingMedications(date);
  };

  function handleSubmit(event, values) {
    showLoading();

    const mutation = saveMutation(values);
    GraphQl(mutation, {})
      .then(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'success',
          t('success.title'),
          t('success.successfully-saved')
        );
        listPendingMedications(new Date());
        listFinishedMedications(new Date());
        loadPendencyQuantity()
          .then((res) => {
            const { userPendency } = res;
            dispatch(updatePendencySummary(userPendency));
          })
          .catch(() => {});
      })
      .catch(() => {
        showNotificationAutoDestroy(
          'danger',
          t('notifications.error'),
          t('notifications.find-record-error')
        );
        hideLoading();
      });

    listFinishedMedications(new Date());
  }

  const handleShow = (event, selected, applicationStatus) => {
    setApplicationType(applicationStatus);
    const updateSelected = selected.map((select) => ({
      ...select,
      applicationStatus,
    }));
    setSelectedRows(updateSelected);
    toggleModal(true);
  };

  const updateApplicationObservation = (obs) => {
    const updatedSelectedRows = selectedRows.map((record) => ({
      ...record,
      observation: obs,
      studentId:
        record.applicationStatus === 'APLI'
          ? record.studentId
          : record.medication.student.id,
    }));
    setSelectedRows(updatedSelectedRows);
    toggleModal();
    handleSubmit(null, updatedSelectedRows);
    updateSelectedDate(selectedDate);
  };

  const onRecipeItemClickHandler = (evt, rowData) => {
    window.open(rowData.medication.photoRecipeUrl);
  };

  const actionsDisabledByConditional = (propss) => {
    if (propss.actions && propss.actions.length) {
      const copyProps = { ...propss };
      copyProps.actions.find(
        (a) => a.name === ACTION_NAME.VIEW_MEDICATION_RECIPE
      ).hidden = !copyProps.data.medication.photoRecipeUrl;
    }
  };

  useEffect(() => {
    listPendingMedications(new Date());
    listFinishedMedications(new Date());
    updatePendingMedicationApplications();
  }, []);
  return (
    <Fragment>
      <MedicalApplicationModal
        title={t(`medication_application.status-reason.${applicationType}`)}
        toggle={toggleModal}
        modal={modal}
        updateApplicationObservation={updateApplicationObservation}
        applicationType={applicationType}
      />
      <Container>
        <Row className="Table">
          <Col md={12} lg={12}>
            <Card>
              <CardBody className="material-table-card">
                <div className="form__form-group--2_columns">
                  <Label className="form__form-label-inline">
                    {format(selectedDate, 'dd/MM/yyyy')}
                  </Label>
                  <DateSelection
                    popperPlacement="top"
                    changeHandler={updateSelectedDate}
                  />
                </div>
                <div className="material-table__wrap">
                  <MatTable
                    data={pendingMedications}
                    columns={ApplicationColumns(t)}
                    onRefreshClick={listPendingMedications}
                    title={t('medication_application.title')}
                    selection
                    onApplyMedicationClick={(evt, selected) =>
                      handleShow(
                        evt,
                        selected,
                        MEDICATION_APPLICATION_STATUS.APPLIED
                      )
                    }
                    onCancelApplyMedicationClick={(evt, selected) =>
                      handleShow(
                        evt,
                        selected,
                        MEDICATION_APPLICATION_STATUS.CANCELED
                      )
                    }
                    onMedicationViewRecipe={onRecipeItemClickHandler}
                    actionsDisabled
                    actionsDisabledByConditional={actionsDisabledByConditional}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <Container>
        <Row className="Table">
          <Col md={12} lg={12}>
            <Card>
              <CardBody>
                <div className="material-table__wrap">
                  <MatTable
                    data={finishedMedications}
                    columns={FinishedColumns(t)}
                    title={t('medication_application.finished')}
                    onRefreshClick={listFinishedMedications}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

MedicationApplication.propTypes = {
  t: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default withTranslation('common')(
  connect((state) => ({
    loading: state.loading,
  }))(MedicationApplication)
);
